import React, { useEffect, useState } from 'react'
import SelectFilter from './document_header/SelectFilter'
import { ModelDoc, PostDocumentModel, TypeModel } from './docs_model'
import DocumentBody from './document_body/DocumentBody'
import { IntlShape, useIntl } from 'react-intl'
import fetchFactory from '../../../../utils/fetchFactory'
import { ContractsModel } from '../../../../types/contracts/contractsModel'
import { configEnum } from '../../../../enums/fetchFactoryEnum'
import { convertDocumentList, convertTypeItem } from './documentMapper'
import Loader from '../../../../components/loader/Loader'
import makeStyles from '@material-ui/core/styles/makeStyles'
import styles from '../../../../styles'

const useStyle = makeStyles((theme) => ({
	...styles(theme),
	loaderContainer: {
		paddingTop: 20,
		backgroundColor: theme.palette.blue['5']
	}
}))

interface AcceptingProps {
	contract: ContractsModel,
	isLargeScreen: boolean,
}

type DocumentsProps = AcceptingProps

const Documents: React.FC<DocumentsProps> = (
	{
		contract,
		isLargeScreen
	}
) => {
	const classes = useStyle()
	const intl: IntlShape = useIntl()
	const currentYear = new Date().getFullYear()
	const selectAll: string = intl.formatMessage({ id: 'contracts.document.selectAll', defaultMessage: 'Tous', description: 'type All text' })

	const [listYear, setListYear] = React.useState<number[]>([currentYear + 1, currentYear, currentYear - 1, currentYear - 2])
	const [listType, setListType] = React.useState<TypeModel[]>([])

	const [year, setYear] = React.useState<number>(listYear[0])
	const [type, setType] = React.useState<string>(selectAll)
	const [docs, setDocs] = React.useState<ModelDoc[]>([])

	const [loadingDocs, setLoadingDocs] = useState<boolean>(false)
	const [loadingYears, setLoadingYears] = useState<boolean>(false)

	useEffect(() => {
		setType(selectAll)
	}, [intl.locale, selectAll])

	//Fetch docs for selected contract for the last 3 years
	useEffect(() => {
		const dataDoc: PostDocumentModel = {
			no_bail: contract.lease,
			years: [currentYear, currentYear - 1, currentYear - 2]
		}

		setLoadingDocs(true)
		fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/contrat/getDocuments`,
			{
				body: JSON.stringify(dataDoc),
				method: configEnum.post
			})
			.then(
				(data: any) => {
					if (!Array.isArray(data)) {
						setDocs(convertDocumentList(Object.values(data)))
					} else {
						setDocs(convertDocumentList(data))
					}
				}
			)
			.finally(
				() => {
					setLoadingDocs(false)
				}
			)

		setLoadingYears(true)
		fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/contrat/getYears`,
			{
				body: JSON.stringify(dataDoc),
				method: configEnum.post
			})
			.then(
				(data: any) => {
					if (Array.isArray(data)) {
						// On trie les dates récuperées puis on ne garde que les 3 premieres pour le filtre
						const tmpList = data.sort((a, b) => a < b ? 1 : -1).slice(0, 3)
						setListYear(
							[
								tmpList[0] + 1,
								...tmpList
							]
						)
					}
				}
			)
			.finally(
				() => {
					setLoadingYears(false)
				}
			)
	}, [contract, currentYear, setLoadingDocs])

	useEffect(() => {
		getTypes(docs)
	}, [docs])

	//Get unique types
	const getTypes = (docs: ModelDoc[]) => {
		const result: TypeModel[] = []
		const map = new Map()
		for (const item of docs) {
			if (!map.has(item.type_document)) {
				map.set(item.type_document, true)
				result.push(convertTypeItem(item))
			}
		}
		setListType(result)
	}

	//Filter doc when type or year is changed
	const filterDocs = (type: string) => {
		if (year === Math.max(...listYear)) {
			return docs.filter(doc => doc.type_document.trim() === type.trim() || type === selectAll)
		}
		// La derniere date (la plus ancienne/petite) apparait comme ex:"2020 et avant"
		// Dans ce cas on affiche tous les docs avec une année inférieur ou égale et dont le type correspond au filtre des types
		if (year === Math.min(...listYear)) {
			return docs.filter(doc => doc.dwstoredatetime.getFullYear() <= year && (doc.type_document.trim() === type.trim() || type === selectAll))
		}
		return docs.filter(doc => doc.dwstoredatetime.getFullYear() === year && (doc.type_document.trim() === type.trim() || type === selectAll))
	}

	return (
		<>
			{
				loadingDocs || loadingYears ?
					<div className={classes.loaderContainer}>
						<Loader />
					</div>
					:
					<>
						<SelectFilter
							listYear={listYear}
							year={year}
							setYear={setYear}
							listType={listType}
							type={type}
							setType={setType}
							selectAll={selectAll}
							isLargeScreen={isLargeScreen}
						/>
						<DocumentBody docs={filterDocs(type)} isLargeScreen={isLargeScreen} />
					</>
			}

		</>
	)
}

export default Documents
